/**
 * Slug helpers shared by the public routes, the admin forms and the backfill
 * script. Keep this file dependency-free so Node can import the same rules.
 */

/** Firestore auto-generated document ids are 20 alphanumeric characters. */
const FIRESTORE_ID_PATTERN = /^[A-Za-z0-9]{20}$/;

/** Slugs that would shadow an existing route or a future one. */
export const RESERVED_SLUGS = new Set([
  'new', 'edit', 'admin', 'auth', 'login', 'register', 'dashboard', 'profile',
  'places', 'tournaments', 'leagues', 'player', 'players', 'friendly-matches',
  'terms', 'privacy', 'cookie-policy', 'stage-access-denied', 'api', 'assets'
]);

export const MAX_SLUG_LENGTH = 60;

/**
 * Romanian comma-below characters are not decomposed by NFD, so they are
 * mapped explicitly. Without this "Sălaj" would slugify to "s-laj".
 */
const CHARACTER_REPLACEMENTS: Record<string, string> = {
  'ș': 's', 'ş': 's', 'Ș': 's', 'Ş': 's',
  'ț': 't', 'ţ': 't', 'Ț': 't', 'Ţ': 't',
  'ß': 'ss', 'æ': 'ae', 'œ': 'oe', 'ø': 'o', 'đ': 'd', 'ł': 'l'
};

export function slugify(value: string): string {
  if (!value) return '';

  const replaced = Array.from(value)
    .map(char => CHARACTER_REPLACEMENTS[char] ?? char)
    .join('');

  return replaced
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/g, '');
}

/** A slug is valid when it round-trips through `slugify` and is not reserved. */
export function isValidSlug(value: string): boolean {
  if (!value) return false;
  if (RESERVED_SLUGS.has(value)) return false;
  if (FIRESTORE_ID_PATTERN.test(value)) return false; // would be read as an id
  return slugify(value) === value;
}

/**
 * Tells a legacy `/places/rPf5NQMG5qMdVIfnhX7M` link apart from a slug one.
 * Slugs always contain a hyphen or are shorter/longer than 20 characters; the
 * only ambiguous case is a single 20-letter word, which `isValidSlug` rejects
 * so such a slug is never handed out in the first place.
 */
export function looksLikeDocumentId(param: string): boolean {
  return FIRESTORE_ID_PATTERN.test(param);
}

/**
 * Tournament names repeat every edition, so the year is part of the base slug:
 * "Cupa de Vară" held in 2026 becomes `cupa-de-vara-2026`.
 */
export function tournamentSlugBase(name: string, date: unknown): string {
  const base = slugify(name);
  const year = extractYear(date);
  if (!base) return year ? `tournament-${year}` : '';
  if (!year || base.endsWith(`-${year}`)) return base;
  return slugify(`${base}-${year}`);
}

/** Accepts Firestore Timestamps, Dates, ISO strings and epoch millis. */
export function extractYear(date: unknown): number | null {
  if (!date) return null;

  const value = date as { seconds?: number; toDate?: () => Date };
  if (typeof value.toDate === 'function') return value.toDate().getFullYear();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000).getFullYear();

  const parsed = new Date(date as string | number | Date);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
}

/**
 * Appends `-2`, `-3`, ... until the candidate is free. Kept here so the admin
 * form and the backfill script disambiguate collisions the same way.
 */
export function nextSlugCandidate(base: string, attempt: number): string {
  if (attempt <= 1) return base;
  const suffix = `-${attempt}`;
  const trimmed = base.slice(0, MAX_SLUG_LENGTH - suffix.length).replace(/-+$/g, '');
  return `${trimmed}${suffix}`;
}
